import React from 'react';
import '../css/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  // Dynamic current year configuration
  const currentYear = new Date().getFullYear();

  return (
    <div className="privacy-master-container">
      <div className="privacy-card">
        
        {/* Header Block */}
        <header className="privacy-header">
          <span className="brand-subtext">LEGAL DOCUMENTATION</span>
          <h1>Privacy Policy</h1>
          <p className="platform-branding"><strong>2look</strong> — Powered by NCN</p>
          <div className="effective-date">Effective Date: July 13, 2026</div>
        </header>

        <hr className="privacy-divider" />

        {/* Content Block */}
        <div className="privacy-body">
          <p className="intro-text">
            Welcome to <strong>2look</strong>, a B2B e-commerce platform designed to connect Vendors, Buyers, and Sales Agents. We are committed to protecting the privacy and security of your corporate and personal data. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you register, access, or trade on our platform.
          </p>
          
          <p className="intro-text">
            By registering an account on 2look, you agree to the collection and use of information in accordance with this policy.
          </p>

          {/* Section 1 */}
          <section className="privacy-section">
            <h2>1. Information We Collect</h2>
            <p>To facilitate B2B commerce and operational features, we collect two types of information:</p>
            
            <h3>A. Enterprise & Business Information (Mandatory for KYC)</h3>
            <ul>
              <li><strong>Company/Enterprise Name:</strong> Official registered business identity.</li>
              <li><strong>Business Identification:</strong> GSTIN (Goods and Services Tax Identification Number) and PAN (Permanent Account Number).</li>
              <li><strong>Financial Records:</strong> Bank Account Numbers, Clearance IDs, and linked financial metadata necessary for payouts, vendor settlements, and commissions.</li>
              <li><strong>Corporate Credentials:</strong> Year of Establishment, Website URL, Business Type, and structural designation labels.</li>
            </ul>

            <h3>B. Contact & User Profiles</h3>
            <ul>
              <li><strong>Representative Details:</strong> Full Representative Name, Official Email Address, Registered Mobile Number, Alternative Mobile Configurations, and WhatsApp Number.</li>
              <li><strong>Operational Metrics:</strong> System Identification Codes (UserCode), Assigned System Roles (e.g., Vendor, Buyer, Sales Agent, Admin).</li>
              <li><strong>Profile Customization:</strong> Profile Pictures/Avatars uploaded to the platform terminal.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="privacy-section">
            <h2>2. How We Use Your Information</h2>
            <p>We process your corporate and account data strictly to manage our operational obligations:</p>
            <ul>
              <li><strong>Verification Pipeline:</strong> Processing application requests to grant 'Approved' status under specified business role architectures.</li>
              <li><strong>Dynamic Stats Trackers:</strong> Logging products supplied (Vendors), products bought (Buyers), and products managed/sold with credited commissions (Sales Agents).</li>
              <li><strong>Financial Operations:</strong> Processing platform trades, processing settlement distributions, and managing payouts.</li>
              <li><strong>Platform Operations:</strong> Maintaining authentication sessions, security checks, and operational profile management.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="privacy-section">
            <h2>3. Data Safety, Retention & Privacy Guard</h2>
            <ul>
              <li><strong>Role-Based Security (Impersonation Protection):</strong> The platform features administrative dashboard override privileges. However, an integrated <strong>Privacy Guard Engine</strong> restricts structural administrative screens from viewing raw uploaded files, binary components, or confidential parameters outside verification scopes.</li>
              <li><strong>Encryption:</strong> Financial transaction channels and associated database structures are routed through secure enterprise protocols to prevent injection, tampering, or leaks.</li>
              <li><strong>Data Retention:</strong> We retain your business records for as long as your corporate account is active or as required by financial auditing laws.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="privacy-section">
            <h2>4. Sharing and Disclosure of Information</h2>
            <p>We do not sell your corporate or identity records. Data sharing occurs only under strict operational boundaries:</p>
            <ul>
              <li><strong>Legal & Statutory Compliance:</strong> To comply with tax laws (GST filings, Income Tax audits) and judiciary requirements.</li>
              <li><strong>Third-Party Payments:</strong> Sharing transaction keys securely with authorized banking channels to complete transfers.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="privacy-section">
            <h2>5. Access and Controls (Rights of Users)</h2>
            <p>Authorized account owners can:</p>
            <ul>
              <li><strong>Modify Profile Information:</strong> Update representative names, designations, and contact parameters at any time via the edit panel.</li>
              <li><strong>Financial Adjustments:</strong> Update Clearance IDs/Bank account details, subject to fresh approval hooks if necessary for anti-fraud practices.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="privacy-section">
            <h2>6. Updates to This Privacy Policy</h2>
            <p>We reserve the right to update this policy to mirror backend updates or compliance changes. Any changes will be published directly on this path with an updated effective date timestamp.</p>
          </section>

          {/* Section 7 */}
          <section className="privacy-section footer-contact-box">
            <h2>7. Contact Support & Governance</h2>
            <p>For privacy audits, compliance issues, or account removal queries regarding <strong>2look (Powered by NCN)</strong>, please contact:</p>
            <div className="contact-details">
              <p>📧 <strong>Email Address:</strong> <a href="mailto:compliance@ncn.com">compliance@ncn.com</a></p>
              <p>🏢 <strong>Corporate Control Office:</strong> NCN Operations Desk</p>
            </div>
          </section>
        </div>

        {/* Footer Attribution */}
        <footer className="privacy-footer-credits">
          <p>© {currentYear} NCN Ltd. / 2look Platform. All Corporate Rights Reserved.</p>
        </footer>

      </div>
    </div>
  );
};

export default PrivacyPolicy;